import { writable } from "svelte/store";
import { type Crumbs } from "$lib/components/Breadcrumbs/Breadcrumbs.svelte";
import { type ApolloQueryResult } from "@apollo/client";
import { type GetNavQuery } from "$lib/gql/genCms/codegen";

type GetPLPCrumbs = (crumbs: Crumbs[]) => Crumbs[];
type PreviousCrumb = (crumb: Crumbs) => void;

type Nav = ApolloQueryResult<GetNavQuery> & {
  previousCrumb?: Crumbs;
};

const navStore = () => {
  const n = writable<Nav>();

  const getPLPCrumbs: GetPLPCrumbs = crumbs => {
    let nav: ApolloQueryResult<GetNavQuery>;

    n.subscribe(navData => {
      nav = navData as ApolloQueryResult<GetNavQuery>;
    });

    /**
     * Last element is the current collection. We want to inject its parent
     * as last-1 element.
     *
     * By its parent I mean how its categorized in the menu tree. e.g:
     *
     * Men/Clothing/Hoddies & Sweatshirts
     *
     * Currently clothing is missing from the breadcrumbs so this function should add that,
     * with a link that goes to the 'view all' clothing page.
     */

    const outer = crumbs[1];
    const collection = crumbs[2];
    let match = nav.data.Nav.items.find(
      (item: any) => item.title.toLowerCase() === outer.title.toLowerCase(),
    );

    if (!match) {
      return crumbs;
    }

    match = match.items.find(
      (item: any) =>
        item.items.filter(
          (childItem: any) =>
            childItem.title.toLowerCase() === collection.title.toLowerCase(),
        ).length > 0,
    );

    if (!match) {
      return crumbs;
    }

    const parent: Crumbs = {
      title: match.title,
      href: `${outer.href}/${match.href}`,
    };

    //Need to inject this into crumbs[crumbs.length - 1]
    const newCrumbs = [...crumbs];
    newCrumbs.splice(crumbs.length - 1, 0, parent);

    return newCrumbs as Crumbs[];
  };

  const previousCrumb: PreviousCrumb = crumb => {
    nav.update(n => {
      if (n) {
        n.previousCrumb = crumb;
      }
      return n;
    });
  };

  return {
    ...n,
    getPLPCrumbs,
    previousCrumb,
  };
};

export const nav = navStore();
